import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { authEvents } from "events/authEvents";

import { setRefresh } from "../storeSlices/login";
import {
  BearerTokensResponse,
  GetV2PostTripJournalsDocumentApiArg,
  GetV2PostTripJournalsDocumentApiResponse,
  GetV2PreTripJournalsDocumentApiArg,
  GetV2PreTripJournalsDocumentApiResponse,
  GetV2ReportsGenerateExamReportApiArg,
  GetV2ReportsGenerateExamReportApiResponse,
  GetV2ReportsGeneratePivotTableApiArg,
  GetV2ReportsGeneratePivotTableApiResponse,
  PostV2ExamsByExamIdConclusionsAndIdSignApiArg,
  PostV2ExamsByExamIdConclusionsAndIdSignApiResponse,
  PostV2FilesOtherUploadApiArg,
  PostV2FilesOtherUploadApiResponse,
  PostV2PersonsByPersonIdPhotosApiArg,
  PostV2PersonsByPersonIdPhotosApiResponse,
  PostV2UsersImportApiArg,
  PostV2UsersImportApiResponse,
} from "./apiRTK";
import { RootState } from "./store";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).login.access;

    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }

    return headers;
  },
});

let currentNetworkLogs: null | Set<string> = null;

if (process.env.NODE_ENV === "development") {
  const fromLocalStorage = localStorage.getItem("networkLogs");
  console.info("init emptyApi: " + process.env.NODE_ENV);
  if (fromLocalStorage) {
    currentNetworkLogs = new Set(JSON.parse(fromLocalStorage));
  } else {
    currentNetworkLogs = new Set<string>();
  }
}

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  const accessToken =
    localStorage.getItem("access") ||
    sessionStorage.getItem("access") ||
    (api.getState() as RootState).login.access;
  const refreshToken =
    localStorage.getItem("refresh") ||
    sessionStorage.getItem("refresh") ||
    (api.getState() as RootState).login.refresh;

  if (process.env.NODE_ENV === "development") {
    if (currentNetworkLogs) {
      currentNetworkLogs.add(api.endpoint);
      localStorage.setItem(
        "networkLogs",
        JSON.stringify(Array.from(currentNetworkLogs))
      );
    }
  }

  if (
    result.error &&
    result.error.status === 401 &&
    accessToken &&
    refreshToken
  ) {
    const newBody = {
      url: "/Authentication/RefreshToken",
      method: "POST",
      body: {
        access: accessToken,

        refresh: refreshToken,
      },
    };
    // try to get a new token
    const refreshResult = await baseQuery(newBody, api, extraOptions);
    if (refreshResult.data) {
      // store the new token
      api.dispatch(setRefresh(refreshResult.data as BearerTokensResponse));
      // retry the initial query
      result = await baseQuery(args, api, extraOptions);
    } else {
      authEvents.emitUnauthorized();
    }
  }
  return result;
};

export const emptySplitApi = createApi({
  baseQuery: baseQueryWithReauth,

  // Вынес сюда эти эндпоинты потому что сгенерированные нормально не работают с файлами,
  // приходится самому корректировать их параметры
  endpoints: (build) => ({
    // Загрузка файла
    uploadFile: build.mutation<
      PostV2FilesOtherUploadApiResponse,
      PostV2FilesOtherUploadApiArg
    >({
      query: (queryArg) => {
        const bodyFormData = new FormData();
        bodyFormData.append("file", queryArg.file || "");

        return {
          url: `/v2/Files/UploadFile`,
          method: "POST",
          body: bodyFormData,
          formData: true,
        };
      },
    }),
    // Подпись заключения
    signConclusion: build.mutation<
      PostV2ExamsByExamIdConclusionsAndIdSignApiResponse,
      PostV2ExamsByExamIdConclusionsAndIdSignApiArg
    >({
      query: (queryArg) => {
        const bodyFormData = new FormData();
        bodyFormData.append("signFile", queryArg.body.signFile || "");

        return {
          url: `/v2/Exams/${queryArg.examId}/Conclusions/${queryArg.id}/Sign`,
          method: "POST",
          body: bodyFormData,
          formData: true,
          params: { certificateId: queryArg.certificateId },
        };
      },
    }),
    // Генерация отчета
    generatePivotReport: build.query<
      GetV2ReportsGeneratePivotTableApiResponse,
      GetV2ReportsGeneratePivotTableApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/Reports/GeneratePivotTable`,
        params: { startDate: queryArg.startDate, endDate: queryArg.endDate },
        responseHandler: (response) => response.blob(), // Добавив вот это свойство все заработало
      }),
    }),
    // Генерация отчета
    generateExamsReport: build.query<
      GetV2ReportsGenerateExamReportApiResponse,
      GetV2ReportsGenerateExamReportApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/Reports/GenerateExamReport`,
        params: { startDate: queryArg.startDate, endDate: queryArg.endDate },
        responseHandler: (response) => response.blob(),
      }),
    }),
    // Генерация журнала
    generatePreTripJournalsDocument: build.query<
      GetV2PreTripJournalsDocumentApiResponse,
      GetV2PreTripJournalsDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/PreTripJournals/Document`,
        params: {
          startDate: queryArg.startDate,
          endDate: queryArg.endDate,
          fileExtension: queryArg.fileExtension,
        },
        responseHandler: (response) => response.blob(),
      }),
    }),
    // Генерация журнала
    generatePostTripJournalsDocument: build.query<
      GetV2PostTripJournalsDocumentApiResponse,
      GetV2PostTripJournalsDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/PostTripJournals/Document`,
        params: {
          startDate: queryArg.startDate,
          endDate: queryArg.endDate,
          fileExtension: queryArg.fileExtension,
        },
        responseHandler: (response) => response.blob(),
      }),
      keepUnusedDataFor: 0, // Отключаем кеширование
    }),
    // Загрузка фотографии
    uploadPersonPhoto: build.mutation<
      PostV2PersonsByPersonIdPhotosApiResponse,
      PostV2PersonsByPersonIdPhotosApiArg
    >({
      query: (queryArg) => {
        const bodyFormData = new FormData();
        bodyFormData.append("file", queryArg.body.file || "");

        return {
          url: `/v2/Persons/${queryArg.personId}/Photos`,
          method: "POST",
          body: bodyFormData,
          formData: true,
        };
      },
    }),
    // Загрузка файла Excel c пользователями
    uploadUsersExcel: build.mutation<
      PostV2UsersImportApiResponse,
      PostV2UsersImportApiArg
    >({
      query: (queryArg) => {
        const bodyFormData = new FormData();
        bodyFormData.append("file", queryArg.body.file || "");

        return {
          url: `/v2/Users/Import`,
          method: "POST",
          body: bodyFormData,
          formData: true,
          params: {
            organizationId: queryArg.organizationId,
            medOrganizationId: queryArg.medOrganizationId,
          },
        };
      },
    }),
  }),
});

export const {
  useUploadFileMutation,
  useSignConclusionMutation,
  useLazyGeneratePivotReportQuery,
  useLazyGenerateExamsReportQuery,
  useLazyGeneratePreTripJournalsDocumentQuery,
  useLazyGeneratePostTripJournalsDocumentQuery,
  useUploadPersonPhotoMutation,
  useUploadUsersExcelMutation,
} = emptySplitApi;
